import { createContext, useContext } from "react";
import { mapStrings } from "../lib/frTypo";

/**
 * On-screen copy for the B2B (partner) video, FR + EN.
 *
 * Sourced from the landing so the video and the page say the same thing:
 *   - hero / steps / pricing / CTA  → i18n/locales `partner.*`
 *   - CAC +60 % in 5 years          → blog "acquisition-cassee-anatomie-plafond" (Paddle)
 *   - 37 % direct traffic           → blog "dark-social-word-of-mouth-attribution"
 *                                      (Marjan et al., JDSMM 2020)
 *   - fixed CAC, 8 € / 2 € split,
 *     40 % of sales, −26 % CAC      → blog "piloter-bouche-a-oreille-comme-paid"
 *                                      (internal, unaudited: said so on screen)
 *   - order-confirmation widget     → partner "use cases" asset
 *
 * House style (same as the site): vouvoiement in FR for brands, no em
 * dashes, no hype words, illustrative numbers labelled as such.
 *
 * `*…*` marks the words painted Frak blue in a headline (see `accented`).
 */

export type Lang = "fr" | "en";

export type Copy = {
  hook: {
    kicker: string;
    value: number;
    line: string;
    sub: string;
    chartTitle: string;
    from: string;
    to: string;
    source: string;
  };
  dark: {
    headlineA: string;
    contact: string;
    status: string;
    incoming1: string;
    incoming2: string;
    reply: string;
    linkTitle: string;
    domain: string;
    headlineB: string;
    sub: string;
    analyticsTitle: string;
    /** Label + share (%) of the illustrative traffic breakdown. Direct first. */
    rows: [string, number][];
    directNote: string;
    footnote: string;
  };
  reveal: { rest: string; sub: string };
  steps: { label: (n: number) => string };
  setCac: {
    kicker: string;
    headline: string;
    sub: string;
    title: string;
    goal: string;
    goals: [string, string, string];
    cacLabel: string;
    budget: string;
    budgetResult: string;
    ambassadors: string;
    frak: string;
  };
  share: {
    kicker: string;
    headline: string;
    sub: string;
    url: string;
    thanks: string;
    confirmed: string;
    badge: string;
    title: string;
    line: string;
    small: string;
    button: string;
    copied: string;
    channels: [string, string, string];
  };
  track: {
    kicker: string;
    headline: string;
    sub: string;
    title: string;
    sales: string;
    revenue: string;
    cac: string;
    fixed: string;
    live: string;
    recommended: string;
    order: string;
    paid: string;
  };
  pricing: {
    headline: string;
    sub: string;
    price: string;
    priceLabel: string;
    struck: [string, string, string];
    cms: string;
  };
  proof: {
    kicker: string;
    upTo: string;
    salesLabel: string;
    cacLabel: string;
    brands: string;
    footnote: string;
  };
  cta: {
    kicker: string;
    line1: string;
    line2: string;
    sub: string;
    button: string;
    url: string;
  };
  /** Formats a euro amount the local way: 1000 → "1 000 €" / "€1,000". */
  money: (value: number, decimals?: number) => string;
  /** Formats a whole number with the local thousands separator. */
  int: (value: number) => string;
  /** " %" (FR, narrow no-break space) or "%" (EN). */
  pct: string;
  /** Tag on every coded dashboard: its figures are an example. */
  sample: string;
};

const frRaw: Copy = {
  hook: {
    kicker: "Votre coût d'acquisition client",
    value: 60,
    line: "en 5 ans.",
    sub: "Chaque nouveau client vous coûte plus cher en publicité.",
    chartTitle: "CAC moyen",
    from: "Il y a 5 ans",
    to: "Aujourd'hui",
    source: "Source : Paddle (ex-ProfitWell)",
  },
  dark: {
    headlineA: "Vos clients vous recommandent *déjà.*",
    contact: "Camille",
    status: "en ligne",
    incoming1: "Elle est top ta crème 😍",
    incoming2: "Tu l'as eue où ??",
    reply: "Ici ! Je l'adore",
    linkTitle: "Crème de jour",
    domain: "votremarque.fr",
    headlineB: "Mais vous ne le *voyez* pas.",
    sub: "Partagée en privé, une recommandation arrive en trafic « direct ». Impossible à piloter.",
    analyticsTitle: "Sources de trafic",
    rows: [
      ["Direct", 37],
      ["Recherche", 33],
      ["Publicité", 18],
      ["Email", 7],
      ["Autres", 5],
    ],
    directNote: "WhatsApp, SMS, DM…",
    footnote:
      "Illustration. 37 % : part moyenne du trafic direct (Marjan et al., Journal of Digital & Social Media Marketing, 2020)",
  },
  reveal: {
    rest: "transforme vos clients en *canal d'acquisition.*",
    sub: "Mesurable, scalable et activé par vos clients.",
  },
  steps: { label: (n) => `Étape ${n}` },
  setCac: {
    kicker: "Campagne",
    headline: "Fixez votre *coût d'acquisition.*",
    sub: "À l'avance. Il ne dépasse jamais.",
    title: "Nouvelle campagne",
    goal: "Objectif",
    goals: ["Ventes", "Opt-in", "Trafic"],
    cacLabel: "Coût d'acquisition par vente",
    budget: "Budget",
    budgetResult: "100 nouveaux clients",
    ambassadors: "Vos ambassadeurs",
    frak: "Frak",
  },
  share: {
    kicker: "Activation",
    headline: "Vos clients partagent un *lien tracké.*",
    sub: "Page produit, confirmation de commande, emails, newsletter…",
    url: "votremarque.fr/merci",
    thanks: "Merci !",
    confirmed: "Votre commande est confirmée",
    badge: "Nouveau",
    title: "Gagnez {gain} grâce à vos partages",
    line: "Recommandez ce produit à vos proches",
    small: "S'ils achètent, ils gagnent… et vous aussi !",
    button: "Partager & gagner {gain}",
    copied: "Lien personnel copié",
    channels: ["WhatsApp", "Instagram", "SMS"],
  },
  track: {
    kicker: "Suivi",
    headline: "Chaque vente est *attribuée.*",
    sub: "Vos ambassadeurs sont payés automatiquement.",
    title: "Performance",
    sales: "Ventes recommandées",
    revenue: "Chiffre d'affaires",
    cac: "CAC",
    fixed: "fixe",
    live: "En direct",
    recommended: "{name} a recommandé",
    order: "Commande {amount}",
    paid: "{gain} versés",
  },
  pricing: {
    headline: "Vous ne payez que sur les *ventes.*",
    sub: "Lancement en moins d'1\u00A0h, sur votre CMS.",
    price: "20 %",
    priceLabel: "de commission sur les ventes générées",
    struck: ["Abonnement", "Frais de setup", "Engagement"],
    cms: "Shopify, WooCommerce, PrestaShop, Magento, WordPress",
  },
  proof: {
    kicker: "Sur nos premières marques",
    upTo: "Jusqu'à",
    salesLabel: "des ventes issues de la recommandation",
    cacLabel: "de coût d'acquisition en moyenne",
    brands: "Déjà en ligne sur Frak",
    footnote:
      "Chiffres internes non audités, constatés sur nos premières marques partenaires.",
  },
  cta: {
    kicker: "Founding Brands",
    line1: "Prenez une longueur d'avance",
    line2: "sur la *Recommendation Economy.*",
    sub: "Accédez à la plateforme en avant-première.",
    button: "Rejoindre la waiting list",
    url: "frak.id/partner",
  },
  money: (v, d = 0) =>
    `${v.toLocaleString("fr-FR", { minimumFractionDigits: d, maximumFractionDigits: d }).replace(/\s/g, "\u202F")}\u00A0€`,
  int: (v) => Math.round(v).toLocaleString("fr-FR").replace(/\s/g, "\u202F"),
  pct: "\u202F%",
  sample: "Exemple",
};

const fr: Copy = mapStrings(frRaw);

const en: Copy = {
  hook: {
    kicker: "Your customer acquisition cost",
    value: 60,
    line: "in 5 years.",
    sub: "Every new customer costs you more in ads.",
    chartTitle: "Average CAC",
    from: "5 years ago",
    to: "Today",
    source: "Source: Paddle (formerly ProfitWell)",
  },
  dark: {
    headlineA: "Your customers *already* recommend you.",
    contact: "Camille",
    status: "online",
    incoming1: "Your skin looks amazing 😍",
    incoming2: "What cream do you use??",
    reply: "This one! Obsessed",
    linkTitle: "Day cream",
    domain: "yourbrand.com",
    headlineB: "But you can't *see* it.",
    sub: "Shared in private, a recommendation lands as “direct” traffic. Impossible to manage.",
    analyticsTitle: "Traffic sources",
    rows: [
      ["Direct", 37],
      ["Search", 33],
      ["Paid ads", 18],
      ["Email", 7],
      ["Other", 5],
    ],
    directNote: "WhatsApp, texts, DMs…",
    footnote:
      "Illustration. 37%: average share of direct traffic (Marjan et al., Journal of Digital & Social Media Marketing, 2020)",
  },
  reveal: {
    rest: "turns your customers into *an acquisition channel.*",
    sub: "Measurable, scalable and powered by your customers.",
  },
  steps: { label: (n) => `Step ${n}` },
  setCac: {
    kicker: "Campaign",
    headline: "Set your *acquisition cost.*",
    sub: "Upfront. It never goes over.",
    title: "New campaign",
    goal: "Goal",
    goals: ["Sales", "Opt-ins", "Traffic"],
    cacLabel: "Acquisition cost per sale",
    budget: "Budget",
    budgetResult: "100 new customers",
    ambassadors: "Your ambassadors",
    frak: "Frak",
  },
  share: {
    kicker: "Activation",
    headline: "Your customers share a *tracked link.*",
    sub: "Product pages, order confirmation, emails, newsletters…",
    url: "yourbrand.com/thank-you",
    thanks: "Thank you!",
    confirmed: "Your order is confirmed",
    badge: "New",
    title: "Earn {gain} by sharing",
    line: "Recommend this product to your friends",
    small: "If they buy, they earn… and so do you!",
    button: "Share & earn {gain}",
    copied: "Personal link copied",
    channels: ["WhatsApp", "Instagram", "Text"],
  },
  track: {
    kicker: "Tracking",
    headline: "Every sale is *attributed.*",
    sub: "Your ambassadors get paid automatically.",
    title: "Performance",
    sales: "Referred sales",
    revenue: "Revenue",
    cac: "CAC",
    fixed: "fixed",
    live: "Live",
    recommended: "{name} recommended",
    order: "Order {amount}",
    paid: "{gain} paid",
  },
  pricing: {
    headline: "You only pay on *sales.*",
    sub: "Go live in under an hour, on your CMS.",
    price: "20%",
    priceLabel: "commission on generated sales",
    struck: ["Subscription", "Setup fees", "Commitment"],
    cms: "Shopify, WooCommerce, PrestaShop, Magento, WordPress",
  },
  proof: {
    kicker: "Across our first brands",
    upTo: "Up to",
    salesLabel: "of sales come from recommendation",
    cacLabel: "acquisition cost, on average",
    brands: "Already live on Frak",
    footnote:
      "Internal, unaudited figures observed across our first partner brands.",
  },
  cta: {
    kicker: "Founding Brands",
    line1: "Get a head start",
    line2: "on the *Recommendation Economy.*",
    sub: "Access the platform ahead of everyone else.",
    button: "Join the waiting list",
    url: "frak.id/partner",
  },
  money: (v, d = 0) =>
    `€${v.toLocaleString("en-US", { minimumFractionDigits: d, maximumFractionDigits: d })}`,
  int: (v) => Math.round(v).toLocaleString("en-US"),
  pct: "%",
  sample: "Example",
};

export const COPY: Record<Lang, Copy> = { fr, en };

export const CopyContext = createContext<Copy>(fr);

export const useCopy = (): Copy => useContext(CopyContext);

/**
 * `"Fixez votre *coût d'acquisition.*"` → the plain text plus the indices
 * of the starred words, in the shape `Words` wants.
 */
export const accented = (
  marked: string,
  color: string,
): { text: string; accents: Record<number, string> } => {
  const accents: Record<number, string> = {};
  let on = false;
  const words = marked.split(" ").map((word, i) => {
    if (word.startsWith("*")) on = true;
    if (on) accents[i] = color;
    if (word.endsWith("*")) on = false;
    return word.replace(/\*/g, "");
  });
  return { text: words.join(" "), accents };
};

/** Replace `{key}` placeholders. */
export const fill = (text: string, values: Record<string, string>) =>
  text.replace(/\{(\w+)\}/g, (_, key: string) => values[key] ?? `{${key}}`);
